import React, { useState } from "react";
import { X, Send } from "lucide-react";

const WA_NUMBER = "919811960890";

function WhatsAppIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.83 14.12c-.25.7-1.45 1.33-2.02 1.42-.52.08-1.17.11-1.89-.12-.44-.14-1-.32-1.71-.63-3.02-1.3-4.99-4.34-5.14-4.54-.15-.2-1.23-1.63-1.23-3.11 0-1.48.78-2.21 1.05-2.51.27-.3.6-.38.8-.38.2 0 .4 0 .57.01.18.01.43-.07.67.51.25.6.85 2.07.92 2.22.08.15.13.33.03.53-.1.2-.15.33-.3.5-.15.18-.32.4-.45.53-.15.15-.31.31-.13.61.18.3.79 1.31 1.7 2.12 1.17 1.04 2.15 1.37 2.46 1.52.3.15.48.13.65-.08.18-.2.75-.88.95-1.18.2-.3.4-.25.67-.15.28.1 1.75.83 2.05.98.3.15.5.22.58.35.07.12.07.72-.18 1.42z" />
    </svg>
  );
}

export default function WhatsAppWidget() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");

  const startChat = () => {
    const text = msg.trim() || "Hi BrandChilly! I'd like to know more.";
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    setMsg("");
  };

  return (
    <>
      {/* Popup */}
      <div
        data-testid="wa-widget-popup"
        className={`fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-[70] w-[calc(100vw-2rem)] max-w-[360px] transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
      >
        <div className="overflow-hidden border border-white/10 shadow-2xl shadow-black/60" style={{ borderRadius: 0 }}>
          {/* Header */}
          <div className="bg-[#0d0d0d] px-4 py-3.5 flex items-center gap-3 border-b border-white/10">
            <div className="relative w-10 h-10 bg-[#C0291A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: 0 }}>
              <span className="font-serif-display text-white text-lg leading-none">B</span>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#25D366] border-2 border-[#0d0d0d] rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium">BrandChilly</div>
              <div className="text-[11px] text-zinc-400">Typically replies within minutes</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white p-1"
              aria-label="Close chat"
              data-testid="wa-widget-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="bg-[#111] px-4 py-5">
            <div className="bg-[#1c1c1c] border border-white/5 px-4 py-3 max-w-[85%]" style={{ borderRadius: 0 }}>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Hi, welcome to <span className="text-white font-medium">BrandChilly</span>.
              </p>
              <p className="text-sm text-zinc-200 leading-relaxed mt-1">
                Please tell us how we can help?
              </p>
              <div className="text-[10px] text-zinc-500 mt-2">Just now</div>
            </div>
          </div>

          {/* Composer */}
          <div className="bg-[#0d0d0d] px-3 py-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && startChat()}
              placeholder="Type your message…"
              className="flex-1 bg-transparent text-sm text-white placeholder-zinc-500 outline-none px-2 py-2"
              data-testid="wa-widget-input"
            />
            <button
              onClick={startChat}
              className="w-10 h-10 flex items-center justify-center bg-[#25D366] text-black hover:bg-[#2be072] transition-colors flex-shrink-0"
              style={{ borderRadius: 0 }}
              aria-label="Start WhatsApp chat"
              data-testid="wa-widget-send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with BrandChilly on WhatsApp"
        data-testid="wa-widget-toggle"
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-[70] w-14 h-14 flex items-center justify-center bg-[#25D366] text-black shadow-lg shadow-[#25D366]/25 hover:scale-105 active:scale-95 transition-transform"
        style={{ borderRadius: "50%" }}
      >
        {open ? <X size={24} /> : <WhatsAppIcon size={28} />}
      </button>
    </>
  );
}
